/**
 * SABA sportsbook API helpers — keeps the raw axios `api` client clean.
 *
 * All endpoints are already implemented by Phase 1 + 2 backend; these
 * are just typed wrappers matching the call-sites the Phase 3 web UI
 * needs.
 *
 * Signing-key partial-update rule
 * -------------------------------
 * `updateAdminSabaConfig` STRIPS `signingKey` from the payload when
 * the caller passes an empty string / undefined / null. This preserves
 * the currently-stored secret and matches the "leave blank to keep
 * current" copy on the admin form.
 */
import api from "./api";

/**
 * Player-facing session launch. Server accepts:
 *   { currency, language, platform }
 * Returns the 200 body directly; caller handles error status via .catch.
 */
export async function sabaSessionLaunch({ currency, language = "en", platform = "web" }) {
  const { data } = await api.post("/saba/session/launch", { currency, language, platform });
  return data;
}

// --------------------------------------------------------------------------- //
// Admin endpoints
// --------------------------------------------------------------------------- //

export async function getAdminSabaConfig() {
  const { data } = await api.get("/admin/saba/config");
  return data;
}

/**
 * Partial upsert of the admin config. Omitting `signingKey` (or passing
 * empty string / null) preserves the currently-stored secret — matches
 * the backend semantics in `providers/saba/admin.py::put_config_handler`.
 */
export async function updateAdminSabaConfig(body) {
  const payload = { ...body };
  if (!payload.signingKey) {
    // Never send an empty signingKey; that would explicitly clear the
    // stored value on the backend upsert.
    delete payload.signingKey;
  }
  const { data } = await api.put("/admin/saba/config", payload);
  return data;
}

export async function getAdminSabaTransactions(params = {}) {
  // Strip empties so we don't send `?type=&currency=` — cleaner logs.
  const q = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== "" && v !== null && v !== undefined) q[k] = v;
  }
  const { data } = await api.get("/admin/saba/transactions", { params: q });
  return data;
}

export async function getAdminSabaSessions(params = {}) {
  const q = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== "" && v !== null && v !== undefined) q[k] = v;
  }
  const { data } = await api.get("/admin/saba/sessions", { params: q });
  return data;
}
